import { Controller } from "@hotwired/stimulus"
import { Drawer } from "flowbite"
import { animate } from "motion"

export default class extends Controller {
  static targets = [ "element" ]

  connect() {
    // Store active slideover instances by ID
    this.slideoverInstances = {}

    // Listen for close events from Turbo Stream actions
    this.handleCloseEvent = (e) => {
      const id = e.detail?.id
      if (id) {
        this.closeById(id)
      }
    }
    document.addEventListener('slideover:close', this.handleCloseEvent)
  }

  // Open a specific slideover
  // Usage: data-action="click->slideover#open" data-slideover-id="my-slideover-id"
  open(event) {
    event.preventDefault()

    // Get slideover ID from data attribute
    const slideoverId = event.currentTarget.dataset.slideoverId
    if (!slideoverId) {
      console.error('Slideover ID not specified. Add data-slideover-id="slideover-id" to the trigger element.')
      return
    }

    // Find the slideover element
    const slideoverElement = document.getElementById(slideoverId)
    if (!slideoverElement) {
      console.error(`Slideover with ID "${slideoverId}" not found.`)
      return
    }

    // Get slideover options from data attributes
    const options = this.getSlideoverOptions(slideoverElement)

    // Create and show slideover instance (using Flowbite Drawer internally)
    const slideoverInstance = new Drawer(slideoverElement, options)
    this.slideoverInstances[slideoverId] = {
      instance: slideoverInstance,
      element: slideoverElement,
      placement: options.placement
    }

    slideoverInstance.show()

    // Animate in
    this.animateIn()

    // Navigate to link if present - load into the turbo frame
    const linkElement = event.currentTarget.closest('a')
    if (linkElement && linkElement.href) {
      const turboFrame = slideoverElement.querySelector(`turbo-frame#${slideoverId}`)
      if (turboFrame) {
        turboFrame.src = linkElement.href
      }
    }
  }

  // Toggle a specific slideover open/closed
  // Usage: data-action="click->slideover#toggle" data-slideover-id="my-slideover-id"
  toggle(event) {
    event.preventDefault()

    const slideoverId = event.currentTarget.dataset.slideoverId
    if (!slideoverId) {
      console.error('Slideover ID not specified. Add data-slideover-id="slideover-id" to the trigger element.')
      return
    }

    // Check if slideover is currently open
    if (this.slideoverInstances[slideoverId]) {
      // Slideover is open, close it
      this.close(event)
    } else {
      // Slideover is closed, open it
      this.open(event)
    }
  }

  // Close a specific slideover or the currently active one
  // Usage: data-action="click->slideover#close" data-slideover-id="my-slideover-id" (optional)
  close(event) {
    event.preventDefault()

    // Get slideover ID from data attribute or find the closest slideover
    let slideoverId = event.currentTarget.dataset.slideoverId

    if (!slideoverId) {
      // Find the closest slideover element
      const slideoverElement = event.currentTarget.closest('[data-slideover-target="element"]')
      if (slideoverElement) {
        slideoverId = slideoverElement.id
      }
    }

    if (!slideoverId || !this.slideoverInstances[slideoverId]) {
      console.error('No active slideover found to close.')
      return
    }

    this.closeById(slideoverId)
  }

  // Close a slideover by ID (called programmatically or from custom events)
  closeById(slideoverId) {
    if (!slideoverId) {
      return
    }

    const slideoverElement = document.getElementById(slideoverId)

    // Try to close via tracked Drawer instance
    if (this.slideoverInstances[slideoverId]) {
      const { instance } = this.slideoverInstances[slideoverId]
      try {
        instance.hide()
        instance.destroy()
      } catch (e) {
        // Drawer instance may be stale after Turbo Stream DOM updates
      }
      delete this.slideoverInstances[slideoverId]
    } else if (slideoverElement) {
      // No tracked instance — create a temporary Drawer to let Flowbite clean up properly
      try {
        const options = this.getSlideoverOptions(slideoverElement)
        const tempDrawer = new Drawer(slideoverElement, options)
        tempDrawer.hide()
        tempDrawer.destroy()
      } catch (e) {
        // Fall through to manual cleanup below
      }
    }

    // Always force-hide the element via DOM as a safety net
    if (slideoverElement) {
      slideoverElement.classList.add('translate-x-full')
      slideoverElement.classList.remove('translate-x-0')
      slideoverElement.setAttribute('aria-hidden', 'true')
    }

    // Clean up any remaining backdrops (both slideover and Flowbite default)
    document.querySelectorAll('.slideover-backdrop, [modal-backdrop]').forEach(el => el.remove())
  }

  getSlideoverOptions(slideoverElement) {
    return {
      placement: slideoverElement.dataset.slideoverPlacement || 'right',
      backdrop: slideoverElement.dataset.slideoverBackdrop !== 'false',
      bodyScrolling: slideoverElement.dataset.slideoverBodyScrolling === 'true',
      edge: slideoverElement.dataset.slideoverEdge === 'true',
      edgeOffset: slideoverElement.dataset.slideoverEdgeOffset || 'bottom-[60px]',
      backdropClasses: 'slideover-backdrop opacity-0 bg-dark-backdrop/50 fixed inset-0 z-40'
    }
  }

  animateIn() {
    // Animate backdrop fade in
    const backdrop = document.querySelector('.slideover-backdrop')
    if (backdrop) {
      animate(
        backdrop,
        { opacity: [ 0, 1 ] },
        { duration: 0.3, easing: 'ease-out' }
      )
    }
  }

  disconnect() {
    // Remove event listener
    document.removeEventListener('slideover:close', this.handleCloseEvent)

    // Cleanup all slideover instances
    Object.values(this.slideoverInstances).forEach(({ instance }) => {
      if (instance) {
        instance.destroy()
      }
    })
    this.slideoverInstances = {}
  }
}
